import {
  InteractionStepType,
  InteractionStepConfig,
  IndexedInteractionStepResponse,
  StepParamsForType,
  StepParamsForTypeWithStage,
} from '@scale/shared/types/chat';
import { IMultiTurnThreadHistory } from '../../ThreadHistory.interface';
import { ProcessSupervisionConfigurationComponent } from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/ProcessSupervision/ProcessSupervisionConfigurationComponent';
import { isConditionallyVisible, isStepVisible } from '@scale/shared/chat/api';

import { ReviewLevel } from '@scale/shared-types';
import { every, isUndefined } from 'lodash';
import StepDescriptor from '../stepDescriptor/StepDescriptor';
import { registerStepDescriptor } from '../stepDescriptor/stepDescriptorContainer';
import ProcessSupervisionViewComponent from './ProcessSupervisionViewComponent';
import ProcessSupervisionPinnedViewComponent from './ProcessSupervisionpinnedViewComponent';
import { schema } from './validation';
import { ToolUseProcessSupervisionDescriptor } from './ToolUseProcessSupervision/ToolUseProcessSupervisionDefinition';
import { ExperimentalToolUseDescriptor } from './ExperimentalToolUseDefinition';

export class ProcessSupervisionDescriptor extends StepDescriptor<InteractionStepType.ProcessSupervision> {
  type = InteractionStepType.ProcessSupervision;

  title = InteractionStepConfig[InteractionStepType.ProcessSupervision].title;
  description = InteractionStepConfig[InteractionStepType.ProcessSupervision].description;

  validationSchema = schema;

  ViewComponent = ProcessSupervisionViewComponent; // Placeholder for actual TaskComponent
  PinnedViewComponent = ProcessSupervisionPinnedViewComponent; // Placeholder for actual PinnedView
  ConfigurationComponent = ProcessSupervisionConfigurationComponent; // Placeholder for actual ConfigurationComponent

  isResponseValid(
    response: IndexedInteractionStepResponse,
    stepParams: StepParamsForTypeWithStage<InteractionStepType.ProcessSupervision>,
    history: IMultiTurnThreadHistory,
    reviewLevel?: ReviewLevel,
  ) {
    if (
      !isStepVisible(stepParams, reviewLevel) ||
      !isConditionallyVisible(
        stepParams,
        history.responses,
        response.index,
        history.threadParams.templateVariables,
        reviewLevel,
      )
    ) {
      return { canSubmit: true, submitTooltip: '' };
    }

    /* Validate that we have at least one chunk */
    if (!response?.context?.chunks?.length) {
      return {
        canSubmit: false,
        submitTooltip: 'Found step with no chunks. Please ensure at least one chunk is provided',
      };
    }

    /* Validate that all chunks have content */
    if (every(response.context.chunks, chunk => isUndefined(chunk.content))) {
      return {
        canSubmit: false,
        submitTooltip: 'Detected empty chunks. Please ensure all chunks have content',
      };
    }

    /* If rating buttons are provided, validate that all chunks have a rating */
    if (stepParams.params.rating_buttons?.length) {
      if (every(response.context.chunks, chunk => isUndefined(chunk.rating))) {
        return {
          canSubmit: false,
          submitTooltip: 'Detected unrated chunks. Please ensure all chunks are rated.',
        };
      }
    }

    if (stepParams.params.require_change_on_rewrites ?? true) {
      for (let i = 0; i < response.context.correctChunks.length; i++) {
        const rewrite = response.context.correctChunks[i].rewrite;
        if (rewrite !== undefined && rewrite === response.context.chunks[i].content) {
          return {
            canSubmit: false,
            submitTooltip: 'Please rewrite the response to be different before submitting.',
          };
        }
      }
    }

    return { canSubmit: true, submitTooltip: '' };
  }

  getResponseTemplatePaths(
    stepParams: StepParamsForType<InteractionStepType.ProcessSupervision>,
    _history: IMultiTurnThreadHistory,
  ) {
    const expectedValues = stepParams?.params?.rating_buttons?.map(r => r.value) || undefined;
    // Implementation to get response template paths
    return [
      {
        path: 'context.chunks[0].content',
        label: 'Chunk 1: Content',
        isProcessSupervision: true,
      },
      {
        path: 'context.chunks[0].rating',
        label: 'Chunk 1: Rating',
        isProcessSupervision: true,
        expectedValues,
      },
    ];
  }

  generateEmptyResponse(
    _stepParams: StepParamsForType<InteractionStepType.ProcessSupervision>,
    history: IMultiTurnThreadHistory,
  ) {
    // const emptyResponse = createDefaultResponseForFields(this.params.params.fields);
    const emptyResponse = {};
    return {
      type: InteractionStepType.ProcessSupervision,
      context: {
        response: emptyResponse,
      },
      output: history.getMostRecentResponse(),
    };
  }

  getConfigurationPreviewTags() {
    // Generate configuration preview tags
    return [];
  }
}

registerStepDescriptor({
  type: InteractionStepType.ProcessSupervision,
  descriptor: new ProcessSupervisionDescriptor(),
});

registerStepDescriptor({
  type: InteractionStepType.ToolUseProcessSupervision,
  descriptor: new ToolUseProcessSupervisionDescriptor(),
});

registerStepDescriptor({
  type: InteractionStepType.ExperimentalToolUse,
  descriptor: new ExperimentalToolUseDescriptor(),
});
